/**
 * Food / drink sticky-label — bitmap renderer.
 *
 * Unlike receipts (which grow downward on an open-ended roll), every label is the
 * SAME fixed size: the full 80mm roll width by a fixed feed length. The content is
 * laid out upright on a design canvas, then the whole thing is rotated 90° so it
 * prints *sideways* along the roll — the classic cup/deli-label look.
 *
 * The layout direction is forced by the rotation: after a 90° turn the design's
 * height becomes the printed width, so the design canvas is `feed × ROLL_WIDTH`
 * (the 576-dot roll width is the design's HEIGHT), and lines stack down that
 * height while each line runs along the (shorter) feed dimension.
 *
 * Reusable across the app: print one via the central service
 * (`printingService.print(PrintType.ITEM_LABEL, data)`), batch them with
 * `printItemLabels` (`item-labels.ts`), or render a PNG for an on-screen preview.
 */
import { wrapText } from './canvas-toolkit';
import type { ItemLabelData } from './types';

const FONT_STACK = "'Helvetica Neue', Arial, sans-serif";

/** 80mm printable width @ 203dpi — the fixed output width (across the roll). */
export const LABEL_ROLL_WIDTH = 576;
/** Fixed feed length of every label, in dots (~50mm @ 203dpi). Tune per media. */
export const LABEL_FEED_DOTS = 400;

/**
 * Rotate the upright design 90° clockwise into the printed bitmap. If a real
 * device prints the label upside-down for the operator, flip this to `false`.
 */
const ROTATE_CLOCKWISE = true;

function toDate(value: ItemLabelData['time']): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

/**
 * Render a single food/drink label to a PNG `data:` URL, sideways on the roll.
 *
 * @param opts.feedDots Override the fixed feed length (defaults to {@link LABEL_FEED_DOTS}).
 */
export function itemLabelCanvas(data: ItemLabelData, opts: { feedDots?: number } = {}): string {
  const feed = opts.feedDots ?? LABEL_FEED_DOTS;

  // Design canvas, upright reading orientation. Its height becomes the printed
  // width after rotation, so height === roll width and width === feed length.
  const designW = feed;
  const designH = LABEL_ROLL_WIDTH;

  const design = document.createElement('canvas');
  design.width = designW;
  design.height = designH;
  const ctx = design.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, designW, designH);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';

  const pad = 16;
  const right = designW - pad;
  const maxW = designW - pad * 2;
  let y = pad;

  const drawLines = (
    text: string,
    size: number,
    weight: number,
    {
      align = 'left',
      indent = 0,
      gapAfter = 8,
    }: { align?: 'left' | 'right'; indent?: number; gapAfter?: number } = {}
  ) => {
    ctx.font = `${weight} ${size}px ${FONT_STACK}`;
    ctx.textAlign = align;
    const x = align === 'right' ? right : pad + indent;
    const arr = wrapText(ctx, text, maxW - indent);
    const lineH = Math.round(size * 1.3);
    arr.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineH));
    y += arr.length * lineH + gapAfter;
  };

  const rule = () => {
    ctx.fillRect(pad, y, maxW, 2);
    y += 2 + 14;
  };

  // ── Header: reference (left) + FOOD/DRINK badge (right) on one row ──
  if (data.reference || data.kind) {
    ctx.font = `700 22px ${FONT_STACK}`;
    if (data.reference) {
      ctx.textAlign = 'left';
      ctx.fillText(data.reference, pad, y);
    }
    if (data.kind) {
      ctx.textAlign = 'right';
      ctx.fillText(data.kind.toUpperCase(), right, y);
    }
    y += Math.round(22 * 1.3) + 8;
    rule();
  }

  // ── Item name (large) ──
  drawLines(data.name, 40, 700, { gapAfter: 6 });

  // ── Quantity ──
  drawLines(`Qty ${data.quantity}`, 30, 700, { gapAfter: 6 });

  // ── Modifiers (small, indented) ──
  (data.modifiers ?? [])
    .filter(Boolean)
    .forEach((mod) => drawLines(mod, 24, 400, { indent: 4, gapAfter: 2 }));

  // ── Time ──
  const d = toDate(data.time);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  y += 8;
  drawLines(`${dateStr} · ${timeStr}`, 24, 600, { gapAfter: 0 });

  // ── Rotate the upright design 90° into the printed bitmap (576 × feed) ──
  const out = document.createElement('canvas');
  out.width = designH;
  out.height = designW;
  const octx = out.getContext('2d')!;
  octx.fillStyle = '#ffffff';
  octx.fillRect(0, 0, out.width, out.height);
  if (ROTATE_CLOCKWISE) {
    octx.translate(out.width, 0);
    octx.rotate(Math.PI / 2);
  } else {
    octx.translate(0, out.height);
    octx.rotate(-Math.PI / 2);
  }
  octx.drawImage(design, 0, 0);

  return out.toDataURL('image/png');
}
